import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, message } from "antd";
import { EllipsisOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import PSAIModal from "@PSAIComponents/PSAIModal";
import ProjectContextMenu from "@view/page_v3/FreeCanvas/Sidebar/components/ProjectContextMenu";
import {
    deleteFlowProject,
    duplicateFlowProject,
    getFlowProjectHistoryImagesBatch,
    saveFlowProject,
} from "@view/page_v3/FreeCanvas/execution";
import { AppContext } from "@utils/AppContext";
import { IMAGE_TYPE } from "@utils/CONST";
import Iconfont from "@utils/Iconfont";
import Utils from "@utils/utils";
import { useNavigate } from "react-router-dom";
import styles from "./index.module.scss";

const CARD_CONTEXT_MENU_WIDTH = 110;
const CARD_CONTEXT_MENU_HEIGHT = 116;
const CARD_CONTEXT_MENU_OFFSET = 8;
const VIEWPORT_SAFE_GAP = 8;

const parseProjectTime = (value = "") => {
    const matched = String(value).match(
        /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/,
    );
    if (!matched) return null;

    const [, year, month, day, hour, minute, second] = matched;
    return new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second),
    );
};

const formatProjectUpdatedText = (value = "") => {
    const updatedAt = parseProjectTime(value);
    if (!updatedAt || Number.isNaN(updatedAt.getTime())) return "";

    const diffMs = Math.max(0, Date.now() - updatedAt.getTime());
    const minuteMs = 60 * 1000;
    const hourMs = 60 * minuteMs;
    const dayMs = 24 * hourMs;
    const monthMs = 30 * dayMs;

    if (diffMs < hourMs) {
        return `${Math.max(1, Math.floor(diffMs / minuteMs))}分钟前 修改`;
    }

    if (diffMs < dayMs) {
        return `${Math.floor(diffMs / hourMs)}小时前 修改`;
    }

    if (diffMs < monthMs) {
        return `${Math.floor(diffMs / dayMs)}天前 修改`;
    }

    return "1个月前 修改";
};

const normalizeImageUrl = (value = "") => String(value || "").replace(/`/g, "").trim();
const cloneValue = (value) => {
    if (typeof structuredClone === "function") return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
};
const INVALID_FILE_NAME_REG = /[\\/:*?"<>|]/g;
const sanitizeFileName = (value = "") => String(value || "").replace(INVALID_FILE_NAME_REG, "_").trim();
const getImageExtension = (blob, url = "") => {
    const mimeExtension = blob?.type?.split("/")?.[1]?.split("+")?.[0];
    if (mimeExtension) return mimeExtension;
    const cleanUrl = String(url || "").split("?")[0];
    const matched = cleanUrl.match(/\.([a-zA-Z0-9]+)$/);
    return matched?.[1] || "png";
};

const pushCandidateImage = (list, value) => {
    const imageUrl = normalizeImageUrl(value);
    if (!imageUrl || list.includes(imageUrl)) return;
    list.push(imageUrl);
};

const getProjectPreviewImages = (project) => {
    const previewImages = [];
    const coverList = Array.isArray(project?.cover)
        ? project.cover
        : (project?.cover ? [project.cover] : []);

    coverList.forEach((item) => pushCandidateImage(previewImages, item));
    if (previewImages.length >= 2) return previewImages.slice(0, 2);

    const nodes = Array.isArray(project?.nodes) ? project.nodes : [];
    nodes.forEach((node) => {
        if (previewImages.length >= 2) return;

        const uploadValues = node?.renderData?.data?.panelState?.uploads
            ? Object.values(node.renderData.data.panelState.uploads)
            : [];
        const imageUrls = Array.isArray(node?.inputParams?.imageUrls) ? node.inputParams.imageUrls : [];

        [
            node?.renderData?.data?.src,
            node?.inputParams?.srcImage,
            node?.inputParams?.refImage,
            node?.inputParams?.commodityImage,
            ...imageUrls,
            ...uploadValues,
        ].forEach((item) => pushCandidateImage(previewImages, item));
    });

    return previewImages.slice(0, 2);
};

const RecentProjectCard = ({
    item,
    onClick,
    onMenuTriggerClick,
    menuOpen = false,
    editing = false,
    editingValue = "",
    renameInputRef,
    onRenameValueChange,
    onRenameSubmit,
    onRenameCancel,
}) => {
    const previewImages = useMemo(() => getProjectPreviewImages(item), [item]);
    const isSinglePreview = previewImages.length <= 1;
    const previewCells = [
        previewImages[0] || "",
        previewImages[1] || "",
    ];

    const handleKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onClick?.();
    };

    return (
        <div
            className={`${styles.projectCard} ${styles.projectCardActionable}`}
            onClick={() => {
                if (editing) return;
                onClick?.();
            }}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        >
            <div className={`${styles.projectPreview} ${isSinglePreview ? styles.projectPreviewSingle : ""}`}>
                {previewCells.map((imageUrl, index) => (
                    <div
                        key={`${item.id}-${index}`}
                        className={`${styles.previewCell} ${!imageUrl ? styles.previewCellEmpty : ""}`}
                    >
                        {imageUrl ? <img src={Utils.getImgThumbnail(imageUrl)} alt="" className={styles.previewImage} /> : (
                            <Iconfont type="icon-aibgOutlined" className={styles.previewEmptyIcon} />
                        )}
                    </div>
                ))}
            </div>
            <div className={styles.projectMeta}>
                <div className={styles.projectTitleRow}>
                    {editing ? (
                        <input
                            ref={renameInputRef}
                            className={styles.renameInput}
                            value={editingValue}
                            maxLength={20}
                            onChange={(event) => onRenameValueChange?.(event.target.value)}
                            onClick={(event) => event.stopPropagation()}
                            onBlur={onRenameSubmit}
                            onKeyDown={(event) => {
                                event.stopPropagation();
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    onRenameSubmit?.();
                                }
                                if (event.key === "Escape") {
                                    event.preventDefault();
                                    onRenameCancel?.();
                                }
                            }}
                        />
                    ) : (
                        <div className={styles.projectTitle} title={item?.name || "未命名画布"}>
                            {item?.name || "未命名画布"}
                        </div>
                    )}
                    <button
                        type="button"
                        className={`${styles.menuTrigger} ${menuOpen ? styles.menuTriggerVisible : ""}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            onMenuTriggerClick?.(event, item);
                        }}
                        aria-label="项目菜单"
                    >
                        <EllipsisOutlined />
                    </button>
                </div>
                <div className={styles.projectTime}>
                    {formatProjectUpdatedText(item?.updatedTime)}
                </div>
            </div>
        </div>
    );
};

const RecentProjectCards = ({ projects = [], onReloadProjects }) => {
    const navigate = useNavigate();
    const { setProSidebarValue } = useContext(AppContext);
    const renameInputRef = useRef(null);
    const visibleProjects = projects.slice(0, 4);
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        projectId: "",
    });
    const [editingProjectId, setEditingProjectId] = useState("");
    const [editingValue, setEditingValue] = useState("");
    const [renameSubmitting, setRenameSubmitting] = useState(false);
    const [deleteModalProjectId, setDeleteModalProjectId] = useState("");
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);

    useEffect(() => {
        if (!contextMenu.visible) return undefined;

        const handlePointerDown = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (target?.closest('[data-free-canvas-sidebar-menu="true"]')) return;
            setContextMenu((prev) => ({ ...prev, visible: false }));
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
        };
    }, [contextMenu.visible]);

    useEffect(() => {
        if (!editingProjectId || !renameInputRef.current) return;
        renameInputRef.current.focus();
        renameInputRef.current.select();
    }, [editingProjectId]);

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }));
    };

    const handleMenuTriggerClick = (event, item) => {
        const triggerRect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const preferredX = triggerRect.right - CARD_CONTEXT_MENU_WIDTH;
        const maxX = Math.max(
            VIEWPORT_SAFE_GAP,
            viewportWidth - CARD_CONTEXT_MENU_WIDTH - VIEWPORT_SAFE_GAP,
        );
        const nextX = Math.min(Math.max(VIEWPORT_SAFE_GAP, preferredX), maxX);
        const bottomSpace = viewportHeight - triggerRect.bottom;
        const nextY = bottomSpace >= CARD_CONTEXT_MENU_HEIGHT + CARD_CONTEXT_MENU_OFFSET
            ? triggerRect.bottom + CARD_CONTEXT_MENU_OFFSET
            : Math.max(
                VIEWPORT_SAFE_GAP,
                triggerRect.top - CARD_CONTEXT_MENU_HEIGHT - CARD_CONTEXT_MENU_OFFSET,
            );

        setContextMenu({
            visible: true,
            x: nextX,
            y: nextY,
            projectId: item.id,
        });
    };

    const handleMenuRename = () => {
        const currentProject = projects.find((item) => item.id === contextMenu.projectId);
        closeContextMenu();
        if (!currentProject) return;

        setEditingProjectId(currentProject.id);
        setEditingValue(currentProject.name || "");
    };

    const handleRenameConfirm = async () => {
        const trimmedValue = editingValue.trim();
        if (!trimmedValue) {
            message.warning("请输入项目名称");
            return;
        }

        const currentProject = projects.find((item) => item.id === editingProjectId);
        if (!currentProject) return;

        setRenameSubmitting(true);
        try {
            await saveFlowProject({
                id: currentProject.id,
                projectName: trimmedValue,
                cover: currentProject.cover || "",
                successMessage: "重命名成功",
            });
            await onReloadProjects?.();
            setEditingProjectId("");
            setEditingValue("");
        } catch (error) {
            console.error(error);
            message.error("重命名失败");
        } finally {
            setRenameSubmitting(false);
        }
    };

    const handleRenameCancel = () => {
        if (renameSubmitting) return;
        setEditingProjectId("");
        setEditingValue("");
    };

    const handleMenuDuplicate = async () => {
        const currentProject = projects.find((item) => item.id === contextMenu.projectId);
        closeContextMenu();
        if (!currentProject) return;

        try {
            const nextProject = await duplicateFlowProject({
                projectId: currentProject.id,
                projectName: currentProject.name || "未命名画布",
                cover: currentProject.cover || "",
                successMessage: "副本已创建",
            });
            await onReloadProjects?.();
            navigate(
                `/home/freeCanvas?project_id=${encodeURIComponent(nextProject.id)}&project_name=${encodeURIComponent(nextProject.name || "")}&force_personal_member=1`,
            );
        } catch (error) {
            console.error(error);
            message.error("创建副本失败");
        }
    };

    const handleMenuDelete = () => {
        closeContextMenu();
        setDeleteModalProjectId(contextMenu.projectId);
    };

    const handleMenuDownload = async () => {
        const currentProject = projects.find((item) => item.id === contextMenu.projectId);
        closeContextMenu();
        if (!currentProject) return;

        try {
            const projectList = await getFlowProjectHistoryImagesBatch({
                projectIds: [currentProject.id],
            });
            const zip = new JSZip();
            let imageCount = 0;

            await Promise.all((Array.isArray(projectList) ? projectList : []).map(async (projectItem, projectIndex) => {
                const projectName = sanitizeFileName(projectItem?.projectName || `项目${projectIndex + 1}`) || `项目${projectIndex + 1}`;
                const nodes = Array.isArray(projectItem?.nodes) ? projectItem.nodes : [];

                await Promise.all(nodes.map(async (nodeItem, nodeIndex) => {
                    const nodeName = sanitizeFileName(nodeItem?.nodeName || `节点${nodeIndex + 1}`) || `节点${nodeIndex + 1}`;
                    const images = Array.isArray(nodeItem?.images) ? nodeItem.images.filter(Boolean) : [];

                    await Promise.all(images.map(async (imageUrl, imageIndex) => {
                        const response = await fetch(imageUrl);
                        if (!response.ok) throw new Error("下载图片失败");

                        const blob = await response.blob();
                        const extension = getImageExtension(blob, imageUrl);
                        zip.file(
                            `${projectName}/${nodeName}-${imageIndex + 1}.${extension}`,
                            blob,
                        );
                        imageCount += 1;
                    }));
                }));
            }));

            if (imageCount === 0) {
                message.warning("未找到可下载的结果图");
                return;
            }

            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, `${sanitizeFileName(currentProject?.name || "AI创意设计结果图") || "AI创意设计结果图"}-${Date.now()}.zip`);
            message.success("下载成功");
        } catch (error) {
            console.error(error);
            message.warning("下载失败，请稍后重试");
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModalProjectId) return;

        setDeleteSubmitting(true);
        try {
            await deleteFlowProject(deleteModalProjectId);
            await onReloadProjects?.();
            setDeleteModalProjectId("");
        } catch (error) {
            console.error(error);
            message.error("删除失败");
        } finally {
            setDeleteSubmitting(false);
        }
    };

    return (
        <>
            <section className={styles.RecentProjectCards}>
                <div className={styles.header}>
                    <div className={styles.title}>最近项目</div>
                    <button
                        type="button"
                        className={styles.allProjects}
                        onClick={() => {
                            setProSidebarValue(IMAGE_TYPE.CREATIVE_IMAGE_GEN);
                            navigate("/home/myProject/list");
                        }}
                    >
                        全部项目
                        <RightOutlined />
                    </button>
                </div>
                <div className={styles.grid}>
                    <button
                        type="button"
                        className={`${styles.projectCard} ${styles.createCard}`}
                        onClick={() => navigate("/home/freeCanvas?skip_auto_select=1&force_personal_member=1")}
                    >
                        <div className={styles.createPreview}>
                            <PlusOutlined className={styles.createIcon} />
                        </div>
                        <div className={styles.projectMeta}>
                            <div className={styles.projectTitle}>新建项目</div>
                        </div>
                    </button>
                    {visibleProjects.map((item) => (
                        <RecentProjectCard
                            key={item.id}
                            item={item}
                            editing={editingProjectId === item.id}
                            editingValue={editingValue}
                            menuOpen={contextMenu.visible && contextMenu.projectId === item.id}
                            onMenuTriggerClick={handleMenuTriggerClick}
                            renameInputRef={editingProjectId === item.id ? renameInputRef : null}
                            onRenameValueChange={setEditingValue}
                            onRenameSubmit={handleRenameConfirm}
                            onRenameCancel={handleRenameCancel}
                            onClick={() => navigate(
                                `/home/freeCanvas?project_id=${encodeURIComponent(item.id)}&project_name=${encodeURIComponent(item.name || "")}&force_personal_member=1`,
                            )}
                        />
                    ))}
                </div>
            </section>
            <ProjectContextMenu
                visible={contextMenu.visible}
                x={contextMenu.x}
                y={contextMenu.y}
                showPin={false}
                onDuplicate={handleMenuDuplicate}
                onRename={handleMenuRename}
                onDownload={handleMenuDownload}
                onDelete={handleMenuDelete}
            />
            <PSAIModal
                title="删除内容"
                open={!!deleteModalProjectId}
                maxWidth={460}
                maskClosable={false}
                closable={!deleteSubmitting}
                footer={(
                    <div className={styles.deleteModalFooter}>
                        <Button
                            className={styles.deleteModalButton}
                            onClick={() => setDeleteModalProjectId("")}
                            disabled={deleteSubmitting}
                        >
                            取消
                        </Button>
                        <Button
                            className={styles.deleteModalButton}
                            type="primary"
                            loading={deleteSubmitting}
                            onClick={handleDeleteConfirm}
                        >
                            删除
                        </Button>
                    </div>
                )}
                onCancel={() => {
                    if (deleteSubmitting) return;
                    setDeleteModalProjectId("");
                }}
            >
                <div className={styles.deleteModalContent}>
                    <div className={styles.deleteModalTitle}>确定要删除此画布项目吗?</div>
                    <div className={styles.deleteModalWarning}>此操作不可复原，请确认是否删除</div>
                </div>
            </PSAIModal>
        </>
    );
};

export default RecentProjectCards;
